<template>
    <div>
        <div v-if="editorVisible" id="new-consent-editor" class="flex items-center gap-2 px-2 relative">
            <multiselect
                v-model="selectedBank"
                class="min-w-64 text-right text-sm"
                placeholder="Velg bank"
                label="name"
                tracked-by="bic"
                :options="sortedBanks"
                select-label=""
                size="small"
                :show-labels="false"
                max-height="200"
            >
                <template #option="{option}">
                    <div class="flex items-center gap-4" :class="{'selected-option': option.name == selectedBank?.name}">
                        <div>{{ option.name }}</div>
                        <i v-if="option.name == selectedBank?.name" class="i-ph-check text-lg text-primary"/>
                    </div>
                </template>
            </Multiselect>
            <input
                v-model="bankUserId"
                class="input input-primary input-sm mr-4 w-32"
                :placeholder="ssnPlaceholder"
            >
            <div class="relative bottom-4">
                <tooltip placement="bottom">
                    <template #tooltipAnchor>
                        <i class="i-ph-question block text-primary text-lg"/>
                    </template>
                    <template #tooltipContent>
                        {{ t('bank-settings.ssn-tooltip') }}
                    </template>
                </tooltip>
            </div>
            <UButton class="w-full" :disabled="initiateConsentDisabled" @click="initiateConsentHandler">
                Initier samtykke
            </UButton>
            <UButton variant="subtle" color="neutral" @click="hideEditor">Avbryt</UButton>
        </div>
        <slot v-else :show-editor="showEditor"/>
    </div>
</template>

<script setup lang="ts">
import Multiselect from 'vue-multiselect'
import {computed, ref} from 'vue'
import _ from 'lodash'
import {Bank} from '@/bank/bank.types'
import {useBanks} from '@/bank/banks.service'
import {selectedOrg} from '@/tenants/org.service'
import {useConsent} from '@/bank/consent/consent.service'
import {useI18n} from 'vue-i18n'
import Tooltip from '@/components/Tooltip.vue'

const {t} = useI18n()
const {initiateConsent} = useConsent()
const {banks} = useBanks()

const editorVisible = ref(false)

const bankUserId = ref()
const selectedBank = ref<Bank>()

const sortedBanks = computed(() => _.sortBy(banks.value, 'name'))

const dnbRegex = RegExp('^[a-zA-Z]{2}\\d{5}$')

const DNB_BIC = 'DNBANOKK'

const isValidSSN = computed(() =>
    (
        selectedBank.value?.bic === DNB_BIC
        && bankUserId.value
        && dnbRegex.test(bankUserId.value)
    )
    ||
    (
        selectedBank.value?.bic !== DNB_BIC
    )
)
const ssnPlaceholder = computed(() => selectedBank.value?.bic === DNB_BIC ? t('bank-settings.dnb') : t('bank-settings.ssn'))
const initiateConsentDisabled = computed(() => !(selectedBank.value || bankUserId.value) || !isValidSSN.value)

const showEditor = () => {
    editorVisible.value = true
}

const hideEditor = () => {
    clear()
    editorVisible.value = false
}

const clear = () => {
    selectedBank.value = undefined
    bankUserId.value = ''
}

const initiateConsentHandler = async () => {
    if (!selectedOrg.value || !selectedBank.value || !bankUserId.value) return
    await initiateConsent(
        selectedOrg.value.organizationNumber,
        selectedBank.value.bic,
        bankUserId.value,
    )
    hideEditor()
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.css" scoped></style>
<style>
#new-consent-editor {

    .multiselect__tags {
        min-height: 20px;
    }

    .multiselect__content {
        background-color: var(--tw-bg-base-100);
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
    }

    .multiselect__option--highlight {
        background-color: var(--tw-bg-base-200);
    }

    .multiselect__input,
    .multiselect__single,
    .multiselect__input::placeholder {
        background-color: var(--tw-bg-base-100);
        color: var(--tw-text-base-content);
    }

    .multiselect * {
        border: none;
        color: var(--tw-text-base-content);
        font-size: 0.875rem;
    }

    .multiselect__content-wrapper {
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }

    .multiselect__option {
        transition: background-color 0.2s;
    }

    .multiselect__option:hover {
        background-color: var(--tw-bg-base-200);
    }

    .multiselect__option--selected {
        background-color: var(--tw-bg-base-100);
    }
}
</style>